// ════════════════════════════════════════════════════════════════════════
//  Die Führung durch die ersten Schritte – als Daten und als Rechnung
//
//  Beim ersten Anmelden beginnt eine Führung, die die Lehrkraft durch die
//  Anwendung trägt und dabei WIRKLICH ihre erste Klasse anlegt, Codes erzeugt
//  und die Kärtchen druckt. Am Ende hat sie kein Verständnis von der Software,
//  sondern eine Klasse, die üben kann.
//
//  DIE SCHRITTE HAKEN SICH AN DEN DATEN AB, NICHT AM KNOPF
//
//  Eine Station gilt als erledigt, wenn DIE SACHE vorliegt - nicht, wenn
//  „Weiter“ gedrückt wurde. Für „erste Klasse“ und „erste Codes“ gibt es eine
//  echte Spur im Bestand, und genau die wird gefragt (`done`). Eine Führung,
//  die „gut gemacht“ sagt, während nichts entstanden ist, verbrennt das
//  Vertrauen der Lehrkraft in genau dem Moment, in dem es aufgebaut werden
//  soll.
//
//  Die Stationen 3 und 4 sind BLICKE, keine Vorgänge. Dafür gibt es keine
//  Datenspur, und eine zu erfinden wäre Tracking. Sie zählen als angesehen -
//  das ist ehrlich und genügt. Sie tragen deshalb `done: null` und sind die
//  einzigen, für die es einen Knopf gibt.
//
//  WARUM DIE ROUTE HIER EIN MUSTER IST UND KEINE FUNKTION
//
//  Die Liste soll DATEN sein: ablesbar, in einem Test aufzuzählen, ohne
//  Ausführung zu verstehen. `:classId` wird von `tourRoute()` ersetzt. Eine
//  Route als Funktion je Station wäre fünfmal dieselbe Zeile mit fünf
//  Gelegenheiten, sie unterschiedlich zu schreiben.
//
//  DIE FÜHRUNG GEHT NIE RÜCKWÄRTS
//
//  Das Lesezeichen (`state.step`) ist eine Höchstmarke: alles davor gilt als
//  erledigt, auch wenn die Datenlage es widerlegt. Sonst stünde eine Lehrkraft,
//  die ihre Probeklasse wieder löscht, plötzlich wieder bei Schritt 1 - ein
//  Bildschirm, der rückwärts geht, ist nicht erklärbar. Dieselbe Überlegung wie
//  bei „eine begonnene Lektion bleibt offen“ in der Lern-App.
//
//  KEIN ZÄHLEN
//
//  Fortgeschrieben wird genau eine Zahl: wie weit DIESE Person ist. Keine
//  Ereignisse, keine Dauer, keine Auswertung.
//
//  WAS HIER STEHT UND WAS IN @otto/onboarding
//
//  Hier: die Stationen dieser App, ihre Texte, ihre Marken und die Rollenfrage.
//  Dort: die Rechnung - Lesezeichen, Prüfliste, Weiterschalten. Die Regeln sind
//  in beiden Apps dieselben, die Stationen in keiner.
// ════════════════════════════════════════════════════════════════════════

import type { Role } from "@otto/plattform";
import type { TourStep as TourStepBase } from "@otto/onboarding";

/**
 * Die Stationen. Sprechende Schlüssel sind hier richtig - anders als bei
 * Lektionen und Stufen liegen sie in keiner Datenbankzeile eines Kindes,
 * sondern in einer Zeile, die zur nächsten Fassung der App gehört.
 */
export type TourStepKey =
  "klasse" | "codes" | "lern-app" | "ergebnisse" | "kolleginnen" | "fertig";

/**
 * Die Marken, die an echten Bedienelementen stehen. Ein eigener Typ, weil zu
 * jeder Marke eine CSS-Regel gehört: eine Marke, die nur in der Liste steht,
 * hebt nichts hervor, und das fällt niemandem auf. Der Wächter in
 * `tour.test.ts` prüft beide Seiten gegeneinander.
 */
export type TourMark = "neue-klasse" | "kaertchen" | "einladen";

/** Woran sich eine Station abhaken lässt – alles aus vorhandenen Abfragen. */
export type TourFacts = {
  /** Hat die Lehrkraft mindestens eine Klasse? Aus `useClasses`. */
  readonly hasClass: boolean;
  /** Hat eine ihrer Klassen Übungscodes? Aus derselben Abfrage (`codeCount`). */
  readonly hasCodes: boolean;
  /**
   * Die Klasse, auf die die Führung zeigt: die erste noch ohne Codes, sonst die
   * erste überhaupt. Null, solange es keine gibt - dann steht in der Leiste ein
   * Ziel ohne Link, und das ist richtig so: es gibt noch nichts zu öffnen.
   */
  readonly classId: string | null;
};

/**
 * Eine Station dieser App.
 *
 * Die Rechnung kommt aus `@otto/onboarding`; hier wird sie nur enger gefasst:
 * der Schlüssel ist eine Union statt `string`, die Marke ebenso, und die Fakten
 * sind die der Verwaltung. Das ist der Grund für den eigenen Typ – ohne ihn
 * ließe sich eine Marke hinschreiben, zu der es keine CSS-Regel gibt, und der
 * Wächter in `TourBar.test.tsx` hätte nichts, woran er sie erkennt.
 */
export type TourStep = TourStepBase<TourStepKey, TourFacts> & {
  readonly mark: TourMark | null;
  /** Für wen diese Station gilt. */
  readonly roles: readonly Role[];
};

const BEIDE: readonly Role[] = ["teacher", "school_admin"];

/**
 * Der Willkommenssatz. Teil der Leiste, keine eigene Station: eine Tour über
 * die Tour ist ein Schritt, der nichts hinterlässt.
 */
export const TOUR_WELCOME =
  "Willkommen bei Otto tippt los. Wir gehen die vier Schritte einmal zusammen durch – " +
  "am Ende steht Ihre erste Klasse, und die Kärtchen liegen gedruckt vor Ihnen.";

export const TOUR_STEPS: readonly TourStep[] = [
  {
    key: "klasse",
    title: "Ihre erste Klasse anlegen",
    text:
      "Eine Klasse ist ein Jahrgang mit einem Schuljahr. Das Schuljahr ist zugleich der " +
      "Anker für die automatische Löschung: am 31. August danach verschwinden Codes und " +
      "Ergebnisse von selbst. Einen Lehrgang wählen Sie hier, weitere können Sie später " +
      "in den Einstellungen der Klasse ergänzen.",
    route: "/classes",
    mark: "neue-klasse",
    roles: BEIDE,
    done: (facts) => facts.hasClass,
  },
  {
    key: "codes",
    title: "Übungscodes erzeugen und Kärtchen drucken",
    text:
      "Kinder haben kein Konto. Jedes übt mit einem Übungscode – welches Kind welchen " +
      "Code hat, steht auf Papier und bleibt bei Ihnen. Erzeugen Sie einen Satz Codes " +
      "und drucken Sie die Kärtchen; das PDF entsteht wirklich.",
    route: "/classes/:classId/codes",
    mark: "kaertchen",
    roles: BEIDE,
    done: (facts) => facts.hasCodes,
  },
  {
    key: "lern-app",
    title: "Was die Kinder sehen",
    text:
      "Öffnen Sie die Lern-App einmal selbst – im Gastmodus, in einem neuen Fenster. " +
      "Mit einem echten Code Ihrer Klasse entstünden Übungsdaten unter einem Kind, das " +
      "es nicht gibt.",
    route: null,
    mark: null,
    roles: BEIDE,
    done: null,
  },
  {
    key: "ergebnisse",
    title: "Ergebnisse und Einstellungen",
    text:
      "Die Ergebnisse sind nach Kind gegliedert, nicht als Bestenliste. In den " +
      "Einstellungen entscheiden Sie, ob die Lektionen der Reihe nach freigegeben " +
      "werden, ob der Tempotest stattfindet und ob ein Lehrgang seinen Vorgänger " +
      "verlangt. Alles steht auf „aus“, und das ist Absicht: Sie schalten ein, was Sie " +
      "brauchen.",
    route: "/classes/:classId/results",
    mark: null,
    roles: BEIDE,
    done: null,
  },
  {
    // Eine Station mehr für den Schuladmin, und sie steht bewusst HINTER den
    // vier gemeinsamen: er ist die erste Person seiner Schule im System und die
    // einzige, die weitere hereinholen kann - aber er soll wissen, wozu er
    // einlädt, bevor er einlädt.
    key: "kolleginnen",
    title: "Kolleginnen und Kollegen einladen",
    text:
      "Sie sind bisher die einzige Person Ihrer Schule im System. Über den Bereich " +
      "„Schule“ laden Sie weitere Lehrkräfte ein; sie bekommen dieselbe Führung und " +
      "sehen ausschließlich ihre eigenen Klassen.",
    route: "/school",
    mark: "einladen",
    roles: ["school_admin"],
    done: null,
  },
  {
    key: "fertig",
    title: "Fertig – jetzt kann es losgehen",
    text:
      "Schneiden Sie die Kärtchen aus, teilen Sie sie aus und notieren Sie sich, welches " +
      "Kind welchen Code hat. Diese Liste bleibt bei Ihnen. Diese Leiste holen Sie " +
      "jederzeit über „Erste Schritte“ im Kopf der Seite zurück.",
    route: null,
    mark: null,
    roles: BEIDE,
    done: null,
  },
];

/** Die Stationen dieser Rolle, in ihrer Reihenfolge. */
export function stepsFor(role: Role): readonly TourStep[] {
  return TOUR_STEPS.filter((step) => step.roles.includes(role));
}

/**
 * Die Platzhalter dieser App für `tourRoute`.
 *
 * Eine eigene Funktion, damit der Name `classId` an genau einer Stelle steht.
 * Die Admin-App bringt `slug` mit – deshalb kennt das Paket keinen der beiden.
 */
export function tourParams(facts: TourFacts): Record<string, string | null> {
  return { classId: facts.classId };
}
